import { createContext, useCallback, useContext, useState } from "react";
import { toast } from "sonner";
import { Check, FileText, Pencil, Plus, Trash2, X } from "lucide-react";
import { ledgerFetch } from "@/lib/ledgerDemoSource";
import { displayName } from "../../lib/linesDisplay";

// ---------------------------------------------------------------------------
// v4 manual edits (PLAN-ledger-v4-ui). Edits apply to the CSV rows, never the fold:
//   POST /api/ledger/v4/edits              -> 201 edit
//   POST /api/ledger/v4/edits/{id}/revoke  -> edit
// Everything here renders only when the page provides an enabled V4EditContext
// (overview.source === "v4" && overview.editable === true).
// ---------------------------------------------------------------------------

// isV4 = the book is v4 (calculation hovers); enabled = v4 and editable (edit controls).
export const V4EditContext = createContext({ enabled: false, isV4: false, version: 0, changed: () => {} });
export const useV4Edit = () => useContext(V4EditContext);

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

async function readError(res) {
  let detail = "";
  try {
    const j = await res.json();
    detail = typeof j?.detail === "string" ? j.detail : "";
  } catch { /* no body */ }
  return `HTTP ${res.status}${detail ? `: ${detail.slice(0, 160)}` : ""}`;
}

export async function postV4Edit(body) {
  const res = await ledgerFetch("/api/ledger/v4/edits", {
    method: "POST", headers: authHeaders(), body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json();
}

export async function revokeV4Edit(id) {
  const res = await ledgerFetch(`/api/ledger/v4/edits/${encodeURIComponent(id)}/revoke`, {
    method: "POST", headers: authHeaders(),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json();
}

// The name /api/documents/* takes. v4 items carry blob_path (null = no blob); older items
// have no blob_path key and keep source_doc_id.
export function docBlobName(x) {
  if (!x) return null;
  if (x.blob_path !== undefined) return x.blob_path || null;
  return x.source_doc_id || null;
}
// Show-doc button gate: a blob to open, or a download link.
export const canShowDoc = (x) => !!(docBlobName(x) || x?.download_url);

const LINE_TYPE_TO_KIND = { capital_call_event_amount: "call", distribution_event_amount: "dist" };
const POINT_KIND_TO_TYPE = { call: "call", distribution: "dist", dist: "dist" };
const TYPE_LABEL = { call: "Call", dist: "Distribution", value: "Value" };

const inputCls = "h-7 px-2 rounded border border-gray-300 bg-white text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-300";
const btnCls = "inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 bg-white text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50";

export function ManualBadge() {
  return (
    <span className="px-1 rounded border border-indigo-200 bg-indigo-50 text-[9px] text-indigo-700" data-testid="manual-badge">
      manual
    </span>
  );
}

// Save helper: runs the POST, shows an inline error, refetches via ctx.changed(), optional Undo toast.
function useSaver(ctx) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = useCallback(async (body, doneMessage) => {
    setBusy(true);
    setError("");
    try {
      const edit = await postV4Edit(body);
      toast.success(doneMessage, {
        action: {
          label: "Undo",
          onClick: async () => {
            try { await revokeV4Edit(edit.id); } catch (e) { toast.error("Undo failed: " + (e?.message || "error")); }
            ctx.changed();
          },
        },
      });
      ctx.changed();
      return true;
    } catch (e) {
      setError(e?.message || "error");
      return false;
    } finally {
      setBusy(false);
    }
  }, [ctx]);
  return { busy, error, run };
}

// One row's Remove / Edit controls. `initial` = {date, type: call|dist|value, amount}.
// `buildFields(values)` -> the edit_row fields object.
function RowControls({ row, leafId, initial, buildFields, rowLabel }) {
  const ctx = useV4Edit();
  const { busy, error, run } = useSaver(ctx);
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(initial.date);
  const [type, setType] = useState(initial.type);
  const [amount, setAmount] = useState(String(initial.amount ?? ""));

  if (!row.row_id) return null;

  const remove = () => run({ kind: "remove_row", leaf_id: leafId, row_id: row.row_id }, "Row removed");
  const save = async (e) => {
    e.preventDefault();
    const n = parseFloat(amount);
    if (!date || Number.isNaN(n)) return;
    const ok = await run({
      kind: "edit_row", leaf_id: leafId, row_id: row.row_id,
      fields: buildFields({ date, type, amount: n }),
    }, "Row updated");
    if (ok) setEditing(false);
  };

  if (editing) {
    return (
      <form onSubmit={save} className="flex flex-wrap items-center gap-1" aria-label={`Edit ${rowLabel}`}>
        <input type="date" aria-label="Date" value={date} onChange={(e) => setDate(e.target.value)} required disabled={busy} className={inputCls} />
        {initial.type !== "value" && (
          <select aria-label="Type" value={type} onChange={(e) => setType(e.target.value)} disabled={busy} className={inputCls}>
            <option value="call">Call</option>
            <option value="dist">Distribution</option>
          </select>
        )}
        <input type="number" step="any" aria-label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} required disabled={busy} className={`${inputCls} w-28`} />
        <button type="submit" disabled={busy} className={btnCls}>Save</button>
        <button type="button" disabled={busy} className={btnCls} onClick={() => { setEditing(false); }}>Cancel</button>
        {error && <span role="alert" className="text-[11px] text-red-600 basis-full">{error}</span>}
      </form>
    );
  }
  return (
    <div className="flex items-center gap-1 justify-end">
      <button type="button" disabled={busy} onClick={() => setEditing(true)} className={btnCls} aria-label={`Edit ${rowLabel}`}>
        <Pencil className="w-3 h-3" /> Edit
      </button>
      <button type="button" disabled={busy} onClick={remove} className={btnCls} aria-label={`Remove ${rowLabel}`}>
        <Trash2 className="w-3 h-3" /> Remove
      </button>
      {error && <span role="alert" className="text-[11px] text-red-600">{error}</span>}
    </div>
  );
}

// ── Lines tab table (replaces LinesTable for v4 editable tenants) ─────────────────
function LineEditRow({ line, fundMap, memberMap, onSelectDoc }) {
  const kind = LINE_TYPE_TO_KIND[line.type] || "call";
  const amount = Math.abs(Number(line.amount) || 0);
  const date = String(line.event_date || "").slice(0, 10);
  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50" data-testid="line-row">
      <td className="px-2 py-2 text-gray-600 whitespace-nowrap max-w-[160px] truncate">{displayName(line.fund, fundMap) || "—"}</td>
      <td className="px-2 py-2 text-gray-600 whitespace-nowrap max-w-[160px] truncate">{displayName(line.investor, memberMap) || "—"}</td>
      <td className="px-2 py-2 font-mono text-gray-600 whitespace-nowrap">{date || "—"}</td>
      <td className="px-2 py-2 whitespace-nowrap">{TYPE_LABEL[kind]}</td>
      <td className="px-2 py-2 text-right font-mono whitespace-nowrap">
        {line.amount == null ? "—" : amount.toLocaleString("en-US")}{" "}
        <span className="text-[10px] text-gray-400">{line.currency || ""}</span>
      </td>
      <td className="px-2 py-2 whitespace-nowrap">
        {line.source === "manual" && <ManualBadge />}
        {line.doc_type && <span className="text-gray-400 ml-1">{line.doc_type}</span>}
      </td>
      <td className="px-2 py-2">
        <RowControls
          row={line}
          leafId={line.leaf_id}
          rowLabel={`line ${date}`}
          initial={{ date, type: kind, amount }}
          buildFields={({ date: d, type, amount: a }) => ({
            file: "cashflows", type, date: d, amount: a,
            ...(line.position_key != null ? { position_key: line.position_key } : {}),
          })}
        />
      </td>
      <td className="px-2 py-2 text-right">
        {onSelectDoc && canShowDoc(line) && (
          <button type="button" onClick={() => onSelectDoc(line)} className="inline-flex p-1 rounded hover:bg-gray-200 text-gray-400" aria-label="View source document" title="View source document">
            <FileText className="w-3.5 h-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
}

export function V4LinesTable({ lines = [], fundMap = {}, memberMap = {}, onSelectDoc = null }) {
  if (lines.length === 0) return <div className="text-xs text-gray-400 py-3 px-3">No ledger lines recorded.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="lines-table">
        <thead className="bg-gray-50 text-gray-500 uppercase">
          <tr>
            <th className="px-2 py-2 text-left font-medium">Fund</th>
            <th className="px-2 py-2 text-left font-medium">Member</th>
            <th className="px-2 py-2 text-left font-medium">Date</th>
            <th className="px-2 py-2 text-left font-medium">Type</th>
            <th className="px-2 py-2 text-right font-medium">Amount</th>
            <th className="px-2 py-2 text-left font-medium">Source</th>
            <th className="px-2 py-2 text-right font-medium">Edit</th>
            <th className="px-2 py-2 w-10" aria-label="Document" />
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <LineEditRow key={l.row_id || i} line={l} fundMap={fundMap} memberMap={memberMap} onSelectDoc={onSelectDoc} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Timeline points (calls, distributions and value points) with the same controls ─
export function V4PointsList({ points = [], leafId, positionKey = null }) {
  const rows = points.filter((p) => p.row_id);
  if (!leafId || rows.length === 0) return null;
  return (
    <div className="mt-2 border border-gray-200 rounded-md bg-white" data-testid="v4-points-list">
      <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">Rows</p>
      <ul className="divide-y divide-gray-100">
        {rows.map((p) => {
          const type = p.kind === "value" ? "value" : (POINT_KIND_TO_TYPE[p.kind] || "call");
          const date = String(p.date || "").slice(0, 10);
          const amount = Math.abs(Number(p.amount) || 0);
          return (
            <li key={p.row_id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 text-xs">
              <span className="flex items-center gap-2">
                <span className="font-mono text-gray-600">{date}</span>
                <span>{TYPE_LABEL[type]}</span>
                <span className="font-mono">{amount.toLocaleString("en-US")}</span>
                {p.source === "manual" && <ManualBadge />}
              </span>
              <RowControls
                row={p}
                leafId={leafId}
                rowLabel={`${TYPE_LABEL[type].toLowerCase()} ${date}`}
                initial={{ date, type, amount }}
                buildFields={({ date: d, type: t, amount: a }) => (
                  type === "value"
                    ? { file: "navs", date: d, value: a, ...(positionKey != null ? { position_key: positionKey } : {}) }
                    : { file: "cashflows", type: t, date: d, amount: a, ...(positionKey != null ? { position_key: positionKey } : {}) }
                )}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Per-leaf "+" add row ─────────────────────────────────────────────────────────
export function V4AddRow({ leafId, positionKey = null }) {
  const ctx = useV4Edit();
  const { busy, error, run } = useSaver(ctx);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("call");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  if (!leafId) return null;

  const submit = async (e) => {
    e.preventDefault();
    const n = parseFloat(amount);
    if (!date || Number.isNaN(n)) return;
    const pk = positionKey != null ? { position_key: positionKey } : {};
    const fields = type === "value"
      ? { file: "navs", date, value: n, ...pk }
      : { file: "cashflows", type, date, amount: n, ...pk };
    const ok = await run({ kind: "add_row", leaf_id: leafId, fields }, "Row added");
    if (ok) { setOpen(false); setAmount(""); setDate(""); }
  };

  return (
    <div className="mb-2">
      <button type="button" onClick={() => setOpen((v) => !v)} className={btnCls} aria-label="Add row" aria-expanded={open}>
        <Plus className="w-3 h-3" />
      </button>
      {open && (
        <form onSubmit={submit} className="flex flex-wrap items-end gap-2 mt-1" aria-label="Add row form">
          <label className="flex flex-col text-[10px] text-gray-500 uppercase tracking-wider">
            Type
            <select value={type} onChange={(e) => setType(e.target.value)} disabled={busy} className={inputCls}>
              <option value="call">Call</option>
              <option value="dist">Distribution</option>
              <option value="value">Value</option>
            </select>
          </label>
          <label className="flex flex-col text-[10px] text-gray-500 uppercase tracking-wider">
            Amount
            <input type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} required disabled={busy} className={`${inputCls} w-32`} />
          </label>
          <label className="flex flex-col text-[10px] text-gray-500 uppercase tracking-wider">
            Date
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required disabled={busy} className={inputCls} />
          </label>
          <button type="submit" disabled={busy} className={btnCls}>Add</button>
          <button type="button" disabled={busy} onClick={() => setOpen(false)} className={btnCls}>Cancel</button>
          {error && <span role="alert" className="text-[11px] text-red-600 basis-full">{error}</span>}
        </form>
      )}
    </div>
  );
}

// ── Per-leaf remove (member row; the leaf is the unit, never a single position) ──
export function V4RemoveLeaf({ leafId }) {
  const ctx = useV4Edit();
  const { busy, error, run } = useSaver(ctx);
  if (!leafId) return null;
  return (
    <>
      <button
        type="button"
        disabled={busy}
        onClick={() => run({ kind: "remove_leaf", leaf_id: leafId }, "Leaf removed")}
        className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 disabled:opacity-50"
        aria-label="Remove leaf"
        title="Remove this leaf from the ledger (Undo is in the confirmation toast)"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
      {error && <span role="alert" className="text-[11px] text-red-600">{error}</span>}
    </>
  );
}

// ── Inline fund rename (fund header; one edit renames the fund on every member leaf) ──
export function V4RenameFund({ leafId, fundName }) {
  const ctx = useV4Edit();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [renamedId, setRenamedId] = useState(null);
  if (!ctx.enabled || !leafId) return null;

  const start = () => { setValue(fundName || ""); setError(""); setEditing(true); };
  const cancel = () => { setEditing(false); setError(""); };
  const save = async () => {
    const name = value.trim();
    if (!name) { setError("Name is required"); return; }
    if (name.length > 200) { setError("Name is too long (max 200)"); return; }
    setBusy(true);
    setError("");
    try {
      const edit = await postV4Edit({ kind: "rename_fund", leaf_id: leafId, fields: { fund: name } });
      setRenamedId(edit?.id || null);
      setEditing(false);
      ctx.changed();
    } catch (e) {
      setError(e?.message || "error");
    } finally {
      setBusy(false);
    }
  };
  const undo = async () => {
    setBusy(true);
    try {
      await revokeV4Edit(renamedId);
      setRenamedId(null);
      ctx.changed();
    } catch (e) {
      setError("Undo failed: " + (e?.message || "error"));
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 flex-shrink-0" data-testid="rename-fund-form">
        <input
          autoFocus
          aria-label="Fund name"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); save(); }
            else if (e.key === "Escape") { e.preventDefault(); cancel(); }
          }}
          className={inputCls}
        />
        <button type="button" disabled={busy} onClick={save} aria-label="Save fund name" className="p-1 rounded hover:bg-green-50 text-green-600 disabled:opacity-50"><Check className="w-3.5 h-3.5" /></button>
        <button type="button" disabled={busy} onClick={cancel} aria-label="Cancel rename" className="p-1 rounded hover:bg-gray-100 text-gray-500"><X className="w-3.5 h-3.5" /></button>
        {error && <span role="alert" className="text-[11px] text-red-600">{error}</span>}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 flex-shrink-0">
      <button type="button" onClick={start} aria-label="Rename fund" title="Rename this fund" className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-700">
        <Pencil className="w-3.5 h-3.5" />
      </button>
      {renamedId && (
        <span className="text-[10px] text-indigo-700" data-testid="renamed-hint">
          renamed{" "}
          <button type="button" disabled={busy} onClick={undo} className="underline hover:text-indigo-900 disabled:opacity-50">Undo</button>
        </span>
      )}
      {error && <span role="alert" className="text-[11px] text-red-600">{error}</span>}
    </span>
  );
}
